import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import { routerShape } from 'react-router/lib/PropTypes';
import { List } from 'immutable';
import withRouter from 'react-router/lib/withRouter';
import _ from 'lodash';

import urls from '../../urls';
import config from '../../config';
import { AccountPages, PointStages, BreakPoints } from '../../constants/AppConstants';
import { translate as t } from '../../lib/I18n';
import User from '../../records/User';
import CustomerActions from '../../actions/CustomerActions';
import connectToStores from '../../helpers/connectToStores';
import SmartcardStore from '../../stores/SmartcardStore';
import FeatureFlagStore from '../../stores/FeatureFlagStore';

import Icon from '../general/Icon';
import PureRenderComponent from '../PureRenderComponent';
import DropDown from './DropDown';
import DropDownItem from './DropDownItem';
import DropDownSubGroup from './DropDownSubGroup';
import Link from '../general/Link';
import FormattedHTMLMessage from '../general/FormattedHTMLMessage';
import ResponsiveRender from '../ResponsiveRender';
import FeatureFlag from '../general/FeatureFlag';
import NavNotificationTrigger from '../notificationCentre/NavTrigger';

function _stateFromStores ({ user }) {
    const smartcard = SmartcardStore.get('smartcard');
    const isReplacementPending = SmartcardStore.isReplacementPending();
    const canUseSmartcard = smartcard && smartcard.isActive() || isReplacementPending;
    const hasSmartcard = !!smartcard;
    const smartcardAwaitingProduct = hasSmartcard && smartcard.isAwaitingProduct();
    const isHotlisted = smartcard && smartcard.isHotlisted();
    const canPreloadKeyGo = FeatureFlagStore.get('enableKeyGoRegistrationAtSignup') && smartcardAwaitingProduct;
    const canRegisterKeyGo = hasSmartcard && smartcard.canRegisterKeyGo();
    const isKeyGo = !!(smartcard && smartcard.getKeyGoProduct());
    const canTransferProduct = ( !smartcard || smartcard.isAwaitingProduct() ) && user.isAdult();
    const redirectOnLogout = FeatureFlagStore.get('enableLogoutRedirect');
    const enableKeyGo = FeatureFlagStore.get('enableKeyGo');
    const enableKeyGoSelfCompleteJourneys = FeatureFlagStore.get('enableKeyGoSelfCompleteJourneys');
    const canAutoDelayRepay = SmartcardStore.canAutoDelayRepay();

    return {
        hasSmartcard,
        canUseSmartcard,
        canTransferProduct,
        canAutoDelayRepay,
        isHotlisted,
        canRegisterKeyGo,
        isKeyGo,
        redirectOnLogout,
        enableKeyGo,
        enableKeyGoSelfCompleteJourneys,
        canPreloadKeyGo,
    };
}

@withRouter
export class LoggedInLinks extends PureRenderComponent {

    static propTypes = {
        user: PropTypes.instanceOf(User).isRequired,
        router: routerShape,
        hasSmartcard: PropTypes.bool,
        canUseSmartcard: PropTypes.bool,
        canTransferProduct: PropTypes.bool,
        canAutoDelayRepay: PropTypes.bool,
        isHotlisted: PropTypes.bool,
        canRegisterKeyGo: PropTypes.bool,
        isKeyGo: PropTypes.bool,
        redirectOnLogout: PropTypes.bool,
        enableKeyGo: PropTypes.bool,
        enableKeyGoSelfCompleteJourneys: PropTypes.bool,
    };

    static defaultProps = {
        hasSmartcard: false,
        canRegisterKeyGo: false,
        enableKeyGoSelfCompleteJourneys: false
    };

    state = { showDropdown: false };

    UNSAFE_componentWillMount () {
        if (SmartcardStore.shouldGetSmartcard()) {
            _.defer(CustomerActions.getSmartcard, this.props.user);
        }
    }

    _toggleDropdown = () => this.setState({ showDropdown: !this.state.showDropdown });

    _closeMenu = () => this.setState({ showDropdown: false });

    _handleLogOut = async () => {
        this._closeMenu();
        if (this.props.redirectOnLogout) {
            this.props.router.push(urls.location(PointStages.SEARCH));
        }
        await CustomerActions.logout(this.props.user);
    };

    _renderDropDownLink (destination, content, options = {}) {
        const [ LinkTag, linkProps ] = options.anchor
            ? ['a', { href: destination, target: '_blank' }]
            : [Link, { to: destination }];
        const testIdKey = options.testIdKey ?? (options.anchor ? null : destination);
        return (
            <DropDownItem key={content}>
                <LinkTag
                    role="menuitem"
                    className="btn"
                    tabIndex="0"
                    { ...(testIdKey && { 'data-test-id': `nav-account-dropdown-item-${testIdKey}` }) }
                    onClick={this._closeMenu}
                    {...linkProps}
                >
                    {options.icon && options.hasIconOnLeft && (
                        <i className={classnames('otrl', options.icon, 'margin-right--xs')} />
                    )}
                    {content}
                    {options.icon && !options.hasIconOnLeft && (
                        <i className={classnames('otrl', options.icon, 'margin-left--xs')} />
                    )}
                </LinkTag>
            </DropDownItem>
        );
    }

    render () {
        const classNames = classnames('btn navlink__account', {
            'active': this.state.showDropdown
        });
        const {
            canAutoDelayRepay,
            canPreloadKeyGo,
            canRegisterKeyGo,
            canTransferProduct,
            canUseSmartcard,
            enableKeyGo,
            enableKeyGoSelfCompleteJourneys,
            hasSmartcard,
            isHotlisted,
            isKeyGo,
            user,
        } = this.props;

        const showPhotocardsRailcardsPage = FeatureFlagStore.get('enableSavedPhotocards') || FeatureFlagStore.get('enableAccountRailcards');
        const brandedMenuNav = new List(config.get('brandedMenuNav'));

        const paperTransferLink = (
            <FeatureFlag name="enableSmartcardPaperTransfers" disabled={ !canTransferProduct }>
                { this._renderDropDownLink(AccountPages.SMARTCARD_TRANSFER, t('account.heading.smartcard.transfer')) }
            </FeatureFlag>
        );

        return (
            <li className="pull-right nav-list__item nav-list__item--account">
                <button className={ classNames }
                        tabIndex="0"
                        type="button"
                        aria-expanded={ this.state.showDropdown }
                        onClick={ this._toggleDropdown }
                        data-test-id="nav-account-button"
                        role="link">
                    <div className="navlink__welcome hidden-mobile hidden-tablet-portrait pull-left">{ t('header.welcome') }</div>
                    <div className="navlink__name truncate pull-left">{ user.firstNames }</div>
                    <Icon icon="dropdown" />
                </button>

                <DropDown show={ this.state.showDropdown } onClose={ this._closeMenu } outsideClickIgnoreClass='navlink__account' testId="nav-account-dropdown">

                    <NavNotificationTrigger closeMenu={ this._closeMenu } />

                    { brandedMenuNav && brandedMenuNav.map(({ name, destination, anchor, icon, hasIconOnLeft, testIdKey }) =>
                        this._renderDropDownLink(destination, t(name), { anchor, icon, hasIconOnLeft, testIdKey } )
                    )}

                    { this._renderDropDownLink(AccountPages.ACCOUNT_DETAILS, t('nav.myaccount'),
                        { testIdKey: 'my-account' }) }

                    <ResponsiveRender minWidth={ BreakPoints.TABLET_PORTRAIT_WIDTH }
                                      minHeight={ BreakPoints.DROPDOWN_SMALL_HEIGHT }>
                        <DropDownSubGroup>
                            { this._renderDropDownLink(AccountPages.ACCOUNT_DETAILS, t('account.nav.accountdetails')) }

                            <FeatureFlag name="enableUserAddresses">
                                { this._renderDropDownLink(AccountPages.ACCOUNT_ADDRESSES, t('account.nav.addresses')) }
                            </FeatureFlag>

                            <FeatureFlag name="enablePay360Api">
                                { this._renderDropDownLink(AccountPages.ACCOUNT_PAYMENT, t('account.nav.paymentdetails')) }
                            </FeatureFlag>

                            <FeatureFlag name="enableEticketAccountSection">
                                { this._renderDropDownLink(AccountPages.ACCOUNT_ETICKETS, t('account.nav.etickets')) }
                            </FeatureFlag>

                            <FeatureFlag name="enableSTickets">
                                <FeatureFlag name="enableSTicketsList">
                                    { this._renderDropDownLink(AccountPages.ACCOUNT_STICKETS, t('account.nav.stickets')) }
                                </FeatureFlag>
                            </FeatureFlag>

                            <FeatureFlag name="enableEVouchers">
                                { this._renderDropDownLink(AccountPages.EVOUCHERS, t('account.nav.evouchers')) }
                            </FeatureFlag>

                            { showPhotocardsRailcardsPage && this._renderDropDownLink(AccountPages.ACCOUNT_PHOTOCARDS_RAILCARDS, t('account.nav.photocards-railcards')) }

                            { this._renderDropDownLink(AccountPages.ACCOUNT_ORDERS_REFUNDS, t('account.nav.orders-refunds')) }

                            <FeatureFlag name="enableDisruptionAlerts">
                                { this._renderDropDownLink(AccountPages.ACCOUNT_DISRUPTION_ALERTS, t('account.nav.disruptionAlerts')) }
                            </FeatureFlag>
                        </DropDownSubGroup>
                    </ResponsiveRender>

                    <FeatureFlag name="enableSmartcard" disabled={ canUseSmartcard }>
                        { hasSmartcard && this._renderDropDownLink(AccountPages.SMARTCARD_APPLY, t('account.heading.smartcard.status')) }
                        { enableKeyGo && canPreloadKeyGo && this._renderDropDownLink(AccountPages.SMARTCARD_KEYGO, t('account.nav.smartcard.keygosignup')) }
                        { !hasSmartcard &&
                            <FeatureFlag name="enableSmartcardAccountApplication">
                                { this._renderDropDownLink(AccountPages.SMARTCARD_APPLY, t('account.heading.smartcard.apply')) }
                            </FeatureFlag>
                        }

                        <FeatureFlag name="enableSmartcardPaperTransfers" disabled={ !canTransferProduct }>
                            <DropDownSubGroup>
                                { paperTransferLink }
                            </DropDownSubGroup>
                        </FeatureFlag>

                        { isHotlisted && (
                            <DropDownSubGroup>
                                { this._renderDropDownLink(AccountPages.SMARTCARD_REPLACEMENT, t('account.nav.smartcard.replacement')) }
                            </DropDownSubGroup>
                        ) }
                    </FeatureFlag>

                    <FeatureFlag name="enableSmartcard" disabled={ !canUseSmartcard }>
                        { this._renderDropDownLink(AccountPages.SMARTCARD_TICKETS, t('account.nav.smartcard'),
                            { testIdKey: 'smartcard-overview' }) }

                        <ResponsiveRender maxWidth={ BreakPoints.TABLET_PORTRAIT_WIDTH }>
                            <DropDownSubGroup>{ paperTransferLink }</DropDownSubGroup>
                        </ResponsiveRender>

                        <ResponsiveRender minWidth={ BreakPoints.TABLET_PORTRAIT_WIDTH }
                                          minHeight={ BreakPoints.DROPDOWN_MEDIUM_HEIGHT }>
                            <DropDownSubGroup>
                                { paperTransferLink }

                                { this._renderDropDownLink(AccountPages.SMARTCARD_TICKETS, t('account.nav.smartcard.tickets')) }

                                { !enableKeyGoSelfCompleteJourneys && this._renderDropDownLink(AccountPages.SMARTCARD_HISTORY, t('account.nav.smartcard.journeyhistory')) }
                                { enableKeyGoSelfCompleteJourneys && this._renderDropDownLink(AccountPages.SMARTCARD_KEYGO_HISTORY, t('account.nav.smartcard.keygohistory')) }

                                { this._renderDropDownLink(AccountPages.SMARTCARD_REPLACEMENT, t('account.nav.smartcard.replacement')) }

                                { enableKeyGo && isKeyGo && this._renderDropDownLink(AccountPages.SMARTCARD_KEYGO,
                                    canRegisterKeyGo ? <FormattedHTMLMessage message='account.nav.smartcard.keygosignup'/> : <FormattedHTMLMessage message='account.smartcard.text.keygo'/>) }
                            </DropDownSubGroup>
                        </ResponsiveRender>
                    </FeatureFlag>

                    <FeatureFlag name="enableSmartcardAssociations">
                        { this._renderDropDownLink(AccountPages.SMARTCARD_LINK, t('account.nav.smartcard.link')) }
                    </FeatureFlag>
                    <FeatureFlag name="enablePassengerAssist">
                        { this._renderDropDownLink(AccountPages.BOOK_PASSENGER_ASSIST, t('account.nav.passengerAssist')) }
                    </FeatureFlag>
                    <FeatureFlag name="enableDelayRepay">
                        { this._renderDropDownLink(config.get('delayRepayBrochureUrl') ?? `https://${config.get('delayRepayHost')}/`, t('account.nav.delayrepay'), { anchor: true }) }

                        {!FeatureFlagStore.get('enableOneClickDelayRepay') && <FeatureFlag name="enableAutoDelayRepay" disabled={ !user.autoDrOptedIn && !canAutoDelayRepay }>
                            <DropDownSubGroup>
                                { this._renderDropDownLink(`https://${config.get('autoDelayRepayHost')}${urls.location(AccountPages.AUTO_DELAY_REPAY).pathname}`, t('account.nav.auto-delay-repay'), { anchor: true }) }
                            </DropDownSubGroup>
                        </FeatureFlag>}
                        <FeatureFlag name="enableOneClickDelayRepay">
                            <DropDownSubGroup>
                                { this._renderDropDownLink(AccountPages.ONE_CLICK_DELAY_REPAY, t('account.nav.one-click-delay-repay')) }
                            </DropDownSubGroup>
                        </FeatureFlag>
                    </FeatureFlag>


                    <DropDownItem>
                        <button aria-label={ t('account.logout') }
                                role="menuitem"
                                type="button"
                                className="btn"
                                tabIndex="0"
                                onClick={ this._handleLogOut }
                                data-role="logout"
                                data-test-id="nav-account-dropdown-item-logout">
                            { t('account.logout') }
                        </button>
                    </DropDownItem>
                </DropDown>

            </li>
        );
    }
}

export default connectToStores([SmartcardStore], _stateFromStores)(LoggedInLinks);
